/**
 * Token system for tailorable HR templates.
 *
 * CRM staff paste template HTML containing {{tokens}} (CRM HR Collections page).
 * When a client hits "Tailor" on the portal HR page, each token is swapped for
 * that client's data (account doc + users doc) and the result is rendered /
 * downloaded as a PDF. Keep this list in sync in one place — both the CRM
 * editor (token reference + sample preview) and the portal modal read from it.
 */

export const TAILOR_TOKENS = [
  { token: 'companyName', label: 'Company name', sample: 'Acme Remodeling Co.' },
  { token: 'logoUrl', label: 'Logo URL', sample: '', hint: 'Use inside an <img src="{{logoUrl}}"> tag' },
  { token: 'ownerName', label: 'Owner / contact name', sample: 'Jane Smith' },
  { token: 'ownerFirstName', label: 'Owner first name', sample: 'Jane' },
  { token: 'ownerLastName', label: 'Owner last name', sample: 'Smith' },
  { token: 'email', label: 'Email', sample: 'jane@acmeremodeling.com' },
  { token: 'phone', label: 'Phone', sample: '(555) 123-4567' },
  { token: 'website', label: 'Website', sample: 'www.acmeremodeling.com' },
  { token: 'streetAddress', label: 'Street address', sample: '123 Main St' },
  { token: 'city', label: 'City', sample: 'Raleigh' },
  { token: 'state', label: 'State', sample: 'NC' },
  { token: 'zipCode', label: 'Zip code', sample: '27601' },
  { token: 'fullAddress', label: 'Full address', sample: '123 Main St, Raleigh, NC 27601' },
  { token: 'primaryColor', label: 'Primary brand color', sample: '#C8102E', hint: 'Hex code — use in style attributes, e.g. style="color:{{primaryColor}}"' },
  { token: 'secondaryColor', label: 'Secondary brand color', sample: '#1F2937', hint: 'Hex code — use in style attributes' },
  { token: 'date', label: "Today's date", sample: 'January 1, 2026' },
];

/** Tokens whose values are colors — the portal modal shows a swatch for these. */
export const COLOR_TOKENS = new Set(['primaryColor', 'secondaryColor']);

/**
 * Pull hex codes out of a free-text brand colors string (the marketing
 * fulfillment "Brand colors (hex or names)" field), e.g. "#C8102E, #1F2937".
 */
export const extractHexColors = (text = '') =>
  String(text).match(/#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g) || [];

export const KNOWN_TOKEN_KEYS = new Set(TAILOR_TOKENS.map((t) => t.token));

const TOKEN_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/** Unique token keys used in an HTML string, in order of first appearance. */
export const extractTokens = (html = '') => {
  const seen = new Set();
  const out = [];
  let match;
  TOKEN_PATTERN.lastIndex = 0;
  while ((match = TOKEN_PATTERN.exec(html)) !== null) {
    if (!seen.has(match[1])) {
      seen.add(match[1]);
      out.push(match[1]);
    }
  }
  return out;
};

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Replace every {{token}} with its value. Values are HTML-escaped (they're
 * plain text / URLs, never markup). Tokens with no value collapse to ''.
 */
export const applyTokens = (html = '', values = {}) =>
  html.replace(TOKEN_PATTERN, (_, key) => escapeHtml(values[key] ?? ''));

const formatToday = () =>
  new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

/**
 * Build token values for a client from their account doc (accounts/{id}) with
 * users-doc fallbacks (profile fields synced by StartProfile). `brand` carries
 * extras resolved elsewhere (e.g. hex codes mined from the marketing
 * fulfillment brandColors field).
 */
export const buildTokenValues = (account = {}, user = {}, brand = {}) => {
  const contact = account.contact || {};
  const address = user.address || {};
  const firstName = contact.firstName || user.firstName || '';
  const lastName = contact.lastName || user.lastName || '';
  const street = account.streetAddress || address.street || '';
  const city = account.city || address.city || '';
  const state = account.state || address.state || '';
  const zipCode = account.zipCode || address.zipCode || '';
  const cityStateZip = [city, [state, zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  return {
    companyName: account.name || account.companyName || account.company || user.company || '',
    logoUrl: account.companyLogo || user.companyLogo || '',
    ownerName: [firstName, lastName].filter(Boolean).join(' ') || contact.name || '',
    ownerFirstName: firstName,
    ownerLastName: lastName,
    email: contact.email || account.email || user.email || '',
    phone: contact.phone || account.phone || account.phoneNumber || user.phone || '',
    website: account.website || user.website || '',
    streetAddress: street,
    city,
    state,
    zipCode,
    fullAddress: [street, cityStateZip].filter(Boolean).join(', '),
    primaryColor: account.brandPrimaryColor || account.primaryColor || brand.primaryColor || '',
    secondaryColor: account.brandSecondaryColor || account.secondaryColor || brand.secondaryColor || '',
    date: formatToday(),
  };
};

/** Sample values for the CRM editor preview. */
export const buildSampleValues = () => {
  const values = {};
  TAILOR_TOKENS.forEach((t) => {
    values[t.token] = t.sample;
  });
  values.date = formatToday();
  return values;
};

/**
 * Base document styles shared by EVERY renderer of tailored HTML — the preview
 * iframe, the offscreen node captured for PDF/PPTX, and the HTML handed to the
 * Word converter. Class-scoped (not body-scoped) so it works when injected
 * into a live page. Keep these in one place: any preview/download divergence
 * means clients get a PDF that doesn't match what they saw.
 */
export const TAILORED_BASE_CSS = `
  .tailored-doc {
    font-family: Georgia, 'Times New Roman', serif;
    color: #111827;
    font-size: 14px;
    line-height: 1.6;
    background: #ffffff;
  }
  .tailored-doc h1, .tailored-doc h2, .tailored-doc h3 { line-height: 1.3; }
  .tailored-doc img { max-width: 100%; }
  .tailored-doc table { border-collapse: collapse; width: 100%; }
  .tailored-doc td, .tailored-doc th { vertical-align: top; }
`;

/**
 * Wrap tailored body HTML in a printable document shell. Used for the preview
 * iframe (srcDoc) and the HTML handed to the Word converter.
 */
export const wrapTailoredHtml = (bodyHtml = '') => `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
${TAILORED_BASE_CSS}
  /* Content width matches the 720px (7.5in) PDF render so line wrapping in
     the preview is identical to the downloaded document. */
  body { margin: 0 auto; padding: 32px; max-width: 720px; }
</style>
</head>
<body class="tailored-doc">${bodyHtml}</body>
</html>`;
